import { useEffect, useRef, useState } from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { FileInputSettingsShape } from '@rabbit/elements/shared-types';
import { t } from 'i18next';
import { UserUploadedDocument } from '@rabbit/data/types';
import { useFileStorage } from '@rabbit/bizproc/react';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { TrashIcon } from '@heroicons/react/24/solid';
import Modal from '../../../molecules/Modals/Modal/Modal';
import { useFormikContext } from 'formik';

export interface InputSelectFileProps {
  name: string;
  settings?: FileInputSettingsShape;
  onChange?: ([...args]: any) => any;
  disabled?: boolean;
}
export function InputSelectFile({
  name,
  settings,
  onChange,
  disabled,
}: InputSelectFileProps) {
  const { loading, accepts, autoUpload, multiple, docType } = settings || {};
  const { setFieldValue, errors, initialValues } = useFormikContext<any>();
  const [currentFiles, setCurrentFiles] = useState<UserUploadedDocument[]>(
    initialValues[name] || []
  );
  const [deletingFile, setDeletingFile] = useState<
    UserUploadedDocument | undefined
  >();
  const {
    uploadFiles,
    uploadedFiles,
    clearAllFilesFromState,
    isUpdating,
    deleteFile,
  } = useFileStorage();

  const ref = useRef(null);

  const confirmationModalSettings = {
    kind: 'pop-up' as const,
    settings: {
      text: t('message.confirmDeleteFile'),
      primaryButtonText: t('general.yes'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => setDeletingFile(undefined),
      handlePrimaryClick: async () => {
        setDeletingFile(undefined);
        await deleteFile(currentFiles[0].url);
        setCurrentFiles((current) => {
          const filtered = current.filter(
            (file) => file.url !== deletingFile?.url
          );
          name && setFieldValue(name, filtered);
          return filtered;
        });
      },
      handleOutlineClick: () => setDeletingFile(undefined),
    },
  };

  const handleUploadFiles = async (files: File[]) => {
    const { activePersona } = useGetMySagePersonas();
    await uploadFiles(files, activePersona?.id, 'note_attachment', docType);
  };

  const localOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosenFiles = Array.prototype.slice.call(e.target.files);
    void handleUploadFiles(chosenFiles);
  };

  useEffect(() => {
    if (uploadedFiles?.filesArr) {
      setCurrentFiles(currentFiles.concat(uploadedFiles.filesArr));
      if (name) {
        if (multiple)
          setFieldValue(name, currentFiles.concat(uploadedFiles.filesArr));
        else setFieldValue(name, uploadedFiles.filesArr[0]);
      }
      clearAllFilesFromState();
    }
  }, [uploadedFiles?.filesArr]);

  const isLoading = () => {
    const loadingClasses = 'inline w-4 h-4 animate-spin text-white';
    return (
      <div className="ml-[-28px] inline-flex h-6 items-center">
        <svg
          aria-hidden="true"
          role="status"
          className={loadingClasses}
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="#E5E7EB"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  };

  return (
    <>
      {autoUpload && currentFiles.length !== 0 && (
        <>
          {currentFiles?.map((file: UserUploadedDocument, index: number) => (
            <div
              className="mb-4 flex flex-col rounded-lg border border-gray-200 p-2"
              key={'upload file ' + index}
            >
              <div className="grid grid-cols-[40px_1fr_40px] gap-2">
                <div className="bg-primary flex items-center justify-center">
                  <img src={file.url} className="text-primary-700 h-8 w-8" />
                </div>
                <div className="flex flex-col">
                  <div className="font-nunito text-base font-bold">
                    {file.ogFilename}
                  </div>
                  <div className="font-nunito text-sm text-gray-500">
                    {file.metadata.timeCreated}
                  </div>
                </div>
                <div className="bg-primary flex cursor-pointer items-center justify-center">
                  <TrashIcon
                    className="h-6 w-6 text-red-500"
                    onClick={() => setDeletingFile(file)}
                  />
                </div>
              </div>
            </div>
          ))}
        </>
      )}
      {!multiple && currentFiles.length > 0 ? (
        <></>
      ) : (
        <>
          <label className="font-nunito bg-primary-700 hover:bg-primary-800 inline-flex w-full flex-shrink-0 grow cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white">
            {loading || isUpdating ? (
              isLoading()
            ) : (
              <ArrowUpTrayIcon className="h-3 w-3" />
            )}
            {loading || isUpdating ? t('Loading...') : t('Upload file')}
            <input
              //{...rest}
              id="file-upload"
              ref={ref}
              multiple={true}
              className="hidden"
              aria-describedby="file_input_help"
              type="file"
              accept={accepts ? accepts.join(',') : 'image/*, .pdf'}
              onChange={autoUpload ? localOnChange : onChange}
              disabled={loading || disabled}
            />
            {name && (errors as any)[name] && (
              <div style={{ color: 'red' }}>{(errors as any)[name]}</div>
            )}
          </label>
          <div className="flex items-center justify-between">
            <p
              className="font-nunito mt-1 text-sm text-gray-500"
              id="file_input_help"
            >
              {t('5MB max file size')}
            </p>
          </div>
        </>
      )}
      {deletingFile && (
        <Modal
          className="h-fit w-[350px] rounded-md bg-white"
          kind={confirmationModalSettings.kind}
          settings={confirmationModalSettings.settings}
        />
      )}
    </>
  );
}

export default InputSelectFile;
